// Player state for the open event: which camera, which mode (alarm clip / live / archive),
// single or quad, and a virtual clock (vtime) for the playhead, bookmarks and ±10 s jumps.
// A seek opens new video sessions; pause / resume only freezes the picture and the clock.

import type { EventAlarm, EventDetail } from '../api.ts';
import { alarmMs } from './util.ts';

export type PlayMode = 'alarm' | 'live' | 'archive';

export interface View {
  eventId: string;
  cameraId: string | null;
  alarmId: string | null;
  mode: PlayMode;
  layout: 'single' | 'quad';
  /** Session start for alarm / archive; null for live. */
  seekAt: number | null;
  /** Wall-clock time of the seek. */
  seekWall: number;
  pausedMs: number;
  pauseStart: number | null;
  synced: boolean;
  /** Bumped to open fresh video sessions at the same position (watch-time view renewal). */
  nonce: number;
  /**
   * Bumped by a new position (seek, live, carrying on after a recording ends). Restating the view from the playhead
   * (atPlayhead: a camera or layout change) keeps it, so feeds that stay on screen play on; only feeds that open then
   * start at the playhead. Absent in views from before 2026-10-29: read as 0.
   */
  epoch?: number;
  /** Phase 2: paused because the operator switched to another event tab (resumes on return). */
  suspended?: boolean;
}

/** Alarm clips start this long before the alarm. */
export const PRE_ROLL_MS = 10_000;

export function vtime(v: View, now: number): number {
  if (v.seekAt === null) return v.pauseStart ?? now;
  const pausedNow = v.pauseStart !== null ? now - v.pauseStart : 0;
  return v.seekAt + (now - v.seekWall - v.pausedMs - pausedNow);
}

export function selectedAlarm(ev: EventDetail, v: View): EventAlarm | undefined {
  return ev.alarms.find((a) => a.id === v.alarmId) ?? ev.alarms[0];
}

export function initialView(ev: EventDetail, now = Date.now()): View {
  const a = ev.alarms[0];
  const cam = a?.cameraId ?? ev.cameras[0]?.id ?? null;
  return {
    eventId: ev.id,
    cameraId: cam,
    alarmId: a?.id ?? null,
    mode: a && a.cameraId ? 'alarm' : 'live',
    layout: 'single',
    seekAt: a && a.cameraId ? alarmMs(a) - PRE_ROLL_MS : null,
    seekWall: now,
    pausedMs: 0,
    pauseStart: null,
    synced: false,
    nonce: 0,
  };
}

/**
 * The same moment, restated from now: seekAt moves to the playhead, keeping pause (live stays live). Video that opens
 * afterwards starts where the timeline is, not back at the selected alarm: switching single / quad and making a tile
 * the main camera go through here (the user, 2026-10-28: after single → quad the video replayed the last alarm).
 */
export function atPlayhead(v: View, now: number): View {
  if (v.seekAt === null) return v;
  return { ...v, seekAt: vtime(v, now), seekWall: now, pausedMs: 0, pauseStart: v.pauseStart !== null ? now : null };
}

/**
 * New video sessions from where the playhead is now, keeping pause. Used when the page comes back
 * after being hidden: the old sessions' watch-time views have ended, so the player needs new ones.
 */
export function renew(v: View, now: number): View {
  return { ...atPlayhead(v, now), nonce: v.nonce + 1 };
}

/** Archive (or alarm clip) from time t, playing. Times in the future become live. */
export function seek(v: View, t: number, now: number, mode: PlayMode = 'archive'): View {
  const epoch = (v.epoch ?? 0) + 1;
  if (t >= now - 1500) return { ...v, mode: 'live', seekAt: null, seekWall: now, pausedMs: 0, pauseStart: null, synced: false, epoch };
  return { ...v, mode, seekAt: t, seekWall: now, pausedMs: 0, pauseStart: null, synced: mode === 'archive' ? v.synced : false, epoch };
}

/** A recorded stream that ends this close to the present carries on as live video. */
export const LIVE_CATCHUP_MS = 120_000;

/**
 * A recorded stream reached its end at `endedAt`. NX sends only what was recorded when the stream was opened (verified
 * 2026-10-26: with or without a duration, an archive request from 40 s ago ends after those 40 s), so an alarm clip runs
 * out shortly after the alarm. Near the present it carries on live; further back (a recording gap, or the 30-minute
 * length of one stream) it opens the recording again from where it stopped. Live views are left alone, so the several
 * feeds of a quad ending together act once.
 */
export function continueAfter(v: View, endedAt: number, now: number): View {
  if (v.mode === 'live' || v.seekAt === null) return v;
  if (now - endedAt <= LIVE_CATCHUP_MS) return goLive(v, now);
  return { ...seek(v, endedAt, now, v.mode), synced: v.synced };
}

export function goLive(v: View, now: number): View {
  return { ...v, mode: 'live', seekAt: null, seekWall: now, pausedMs: 0, pauseStart: null, synced: false, epoch: (v.epoch ?? 0) + 1 };
}

export function togglePause(v: View, now: number): View {
  if (v.pauseStart === null) return { ...v, pauseStart: now };
  return { ...v, pausedMs: v.pausedMs + (now - v.pauseStart), pauseStart: null };
}

export function toIso(t: number | null): string | null {
  return t === null ? null : new Date(t).toISOString();
}

/** Leaving this event's tab: freeze the playhead where it is (live stays live). */
export function suspend(v: View, now: number): View {
  if (v.mode === 'live' || v.pauseStart !== null) return v;
  return { ...v, pauseStart: now, suspended: true };
}

/** Back on this event's tab: resume a tab-switch pause and open fresh sessions at the playhead. */
export function wake(v: View, now: number): View {
  const resumed = v.suspended ? { ...togglePause(v, now), suspended: false } : v;
  return renew(resumed, now);
}
